import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { useInviteAdmin } from '../../queries/adminUsers';

interface Props {
  onClose: () => void;
}

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';

// Role → Cognito group (the RBAC layer maps these groups back to roles).
const GROUP_OPTIONS = [
  { value: 'super-admin', label: 'Super admin' },
  { value: 'admin', label: 'Operations' },
  { value: 'financial-admin', label: 'Support' },
  { value: 'readonly-admin', label: 'Analyst (read-only)' },
];

export function InviteAdminDialog({ onClose }: Props) {
  const invite = useInviteAdmin();
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', group: 'readonly-admin' });

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = () => {
    if (!form.email || !form.first_name || !form.last_name) {
      setError('Email, first name and last name are all required.');
      return;
    }
    setError(null);
    invite.mutate(
      { email: form.email, first_name: form.first_name, last_name: form.last_name, cognito_groups: [form.group] },
      {
        onSuccess: (res) => setTempPassword(res.temporaryPassword),
        onError: (err) => setError(extractErrorMessage(err, 'Failed to invite admin.')),
      },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-background p-6 shadow-lg">
          {tempPassword ? (
            <>
              <Dialog.Title className="text-lg font-semibold">Admin invited</Dialog.Title>
              <p className="mt-1 text-sm text-muted-foreground">
                Give this one-time temporary password to {form.email}. It is shown only now — they'll be forced to change it on first login.
              </p>
              <div className="mt-4 rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm">{tempPassword}</div>
              <div className="mt-6 flex justify-end">
                <button type="button" onClick={onClose} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90">
                  Done
                </button>
              </div>
            </>
          ) : (
            <>
              <Dialog.Title className="text-lg font-semibold">Invite admin</Dialog.Title>
              <p className="mt-1 text-sm text-muted-foreground">Creates a Cognito account (email suppressed) and returns a one-time temporary password.</p>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <label className="col-span-2 block text-sm">
                  Email
                  <input type="email" className={inputClass} value={form.email} onChange={(e) => set({ email: e.target.value })} />
                </label>
                <label className="block text-sm">
                  First name
                  <input className={inputClass} value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} />
                </label>
                <label className="block text-sm">
                  Last name
                  <input className={inputClass} value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} />
                </label>
                <label className="col-span-2 block text-sm">
                  Role
                  <select className={inputClass} value={form.group} onChange={(e) => set({ group: e.target.value })}>
                    {GROUP_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>

                {error && <p className="col-span-2 text-sm text-destructive">{error}</p>}
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={invite.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                  {invite.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Invite
                </button>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
